import { LODLevel, CameraViewport, onLODChangeCallback } from '../Types';
import { ViewportCalculator } from '../ViewportCalculator';
import { DEFAULT_LOD_LEVELS } from './constants';

/**
 * LOD transition state
 */
export enum TransitionState {
  STABLE = 'stable',
  TRANSITIONING = 'transitioning',
  COMPLETING = 'completing',
}

/**
 * LOD change event data
 */
export interface LODChangeEvent {
  oldLevel: number;
  newLevel: number;
  timestamp: number;
  isRapidTransition: boolean;
}

/**
 * Events emitted by LODController
 */
export interface LODControllerEvents {
  lodChanged: (event: LODChangeEvent) => void;
  transitionStarted: (targetLevel: number) => void;
  transitionCompleted: (level: number) => void;
}

/**
 * LOD controller statistics
 */
export interface LODControllerStats {
  currentLevel: number;
  targetLevel: number;
  transitionState: TransitionState;
  transitionStartTime: number;
  transitionDuration: number;
  totalTransitions: number;
  rapidTransitions: number;
}

/**
 * Manages LOD level transitions and state
 */
export class LODController {
  private currentLODLevel: LODLevel;
  private targetLODLevel: number;
  private transitionState: TransitionState = TransitionState.STABLE;
  private transitionStartTime = 0;
  
  private readonly lodLevels: LODLevel[];
  private readonly onLODChangeCallbacks: onLODChangeCallback[] = [];
  private readonly eventHandlers = new Map<keyof LODControllerEvents, Set<Function>>();
  
  // Statistics
  private totalTransitions = 0;
  private rapidTransitions = 0;

  constructor(lodLevels: LODLevel[] = DEFAULT_LOD_LEVELS) {
    this.lodLevels = [...lodLevels];
    this.currentLODLevel = this.lodLevels[0];
    this.targetLODLevel = this.currentLODLevel.level;
  }

  /**
   * Evaluate viewport and potentially change LOD level
   */
  evaluateViewport(viewport: CameraViewport): boolean {
    const newLODLevel = ViewportCalculator.calculateLODLevel(viewport.fov, this.lodLevels);
    
    if (newLODLevel.level !== this.currentLODLevel.level) {
      return this.changeLODLevel(newLODLevel);
    }
    
    return false;
  }

  /**
   * Change LOD level with transition management
   */
  private changeLODLevel(newLODLevel: LODLevel): boolean {
    const oldLevel = this.currentLODLevel.level;
    const newLevel = newLODLevel.level;
    
    // Check if this is a rapid transition
    const isRapidTransition = Math.abs(newLevel - oldLevel) > 1;
    
    // Update target LOD level (tracks final destination during rapid transitions)
    this.targetLODLevel = newLevel;
    
    // Start transition if not already transitioning
    if (this.transitionState === TransitionState.STABLE) {
      this.beginTransition();
    }
    
    // Update current LOD level
    this.currentLODLevel = newLODLevel;
    
    // Update statistics
    this.totalTransitions++;
    if (isRapidTransition) {
      this.rapidTransitions++;
    }
    
    // Create event data
    const event: LODChangeEvent = {
      oldLevel,
      newLevel,
      timestamp: Date.now(),
      isRapidTransition,
    };
    
    // Notify callbacks
    this.notifyLODChange(oldLevel, newLevel);
    this.emit('lodChanged', event);
    
    return true;
  }

  /**
   * Begin LOD transition
   */
  private beginTransition(): void {
    this.transitionState = TransitionState.TRANSITIONING;
    this.transitionStartTime = Date.now();
    this.emit('transitionStarted', this.targetLODLevel);
  }

  /**
   * Check if LOD level has stabilized (no more changes expected)
   */
  isLevelStable(): boolean {
    return this.currentLODLevel.level === this.targetLODLevel;
  }

  /**
   * Check if currently in a rapid transition
   */
  isRapidTransition(): boolean {
    return Math.abs(this.targetLODLevel - this.currentLODLevel.level) > 1;
  }

  /**
   * Get time elapsed since transition started
   */
  getTransitionElapsedTime(): number {
    if (this.transitionState === TransitionState.STABLE) {
      return 0;
    }
    return Date.now() - this.transitionStartTime;
  }

  /**
   * Mark transition as completing
   */
  beginTransitionCompletion(): void {
    if (this.transitionState === TransitionState.TRANSITIONING) {
      this.transitionState = TransitionState.COMPLETING;
    }
  }

  /**
   * Complete the LOD transition
   */
  completeTransition(): void {
    if (this.transitionState !== TransitionState.STABLE) {
      this.transitionState = TransitionState.STABLE;
      this.emit('transitionCompleted', this.currentLODLevel.level);
    }
  }

  /**
   * Force set LOD level (for debugging or manual control)
   */
  setLODLevel(level: number): boolean {
    const lodLevel = this.lodLevels.find((lod) => lod.level === level);
    if (!lodLevel) {
      return false;
    }

    return this.changeLODLevel(lodLevel);
  }

  /**
   * Update LOD levels configuration
   * This replaces the entire LOD levels array with new configuration
   */
  setLODLevels(lodLevels: LODLevel[]): void {
    if (!lodLevels || lodLevels.length === 0) {
      throw new Error('LOD levels array cannot be empty');
    }

    // Clear the existing levels
    this.lodLevels.length = 0;
    this.lodLevels.push(...lodLevels);

    // Reset to the first level
    this.currentLODLevel = this.lodLevels[0];
    this.targetLODLevel = this.currentLODLevel.level;

    // Reset transition state
    this.transitionState = TransitionState.STABLE;
    this.transitionStartTime = 0;

    // Reset statistics as they're no longer valid
    this.resetStats();
  }

  /**
   * Get current LOD level
   */
  getCurrentLODLevel(): LODLevel {
    return this.currentLODLevel;
  }

  /**
   * Get target LOD level
   */
  getTargetLODLevel(): number {
    return this.targetLODLevel;
  }

  /**
   * Get current transition state
   */
  getTransitionState(): TransitionState {
    return this.transitionState;
  }

  /**
   * Get all available LOD levels
   */
  getLODLevels(): LODLevel[] {
    return [...this.lodLevels];
  }

  /**
   * Get LOD level by level number
   */
  getLODLevelByNumber(level: number): LODLevel | undefined {
    return this.lodLevels.find((lod) => lod.level === level);
  }

  /**
   * Get next higher LOD level
   */
  getNextHigherLOD(): LODLevel | undefined {
    const currentIndex = this.lodLevels.findIndex((lod) => lod.level === this.currentLODLevel.level);
    return currentIndex < this.lodLevels.length - 1 ? this.lodLevels[currentIndex + 1] : undefined;
  }

  /**
   * Get next lower LOD level
   */
  getNextLowerLOD(): LODLevel | undefined {
    const currentIndex = this.lodLevels.findIndex((lod) => lod.level === this.currentLODLevel.level);
    return currentIndex > 0 ? this.lodLevels[currentIndex - 1] : undefined;
  }

  /**
   * Calculate expected tiles per face for current LOD
   */
  getExpectedTilesPerFace(): number {
    return this.currentLODLevel.tilesPerFace * this.currentLODLevel.tilesPerFace;
  }

  /**
   * Calculate total expected tiles for current LOD (all 6 faces)
   */
  getTotalExpectedTiles(): number {
    return this.getExpectedTilesPerFace() * 6;
  }

  /**
   * Check if FOV is within current LOD threshold
   */
  isFOVInCurrentThreshold(fov: number): boolean {
    const threshold = this.currentLODLevel.fovThreshold;
    return fov >= threshold.min && fov <= threshold.max;
  }

  /**
   * Get recommended LOD level for given FOV (without changing current level)
   */
  getRecommendedLODLevel(fov: number): LODLevel {
    return ViewportCalculator.calculateLODLevel(fov, this.lodLevels);
  }

  /**
   * Add LOD change callback
   */
  addLODChangeCallback(callback: onLODChangeCallback): void {
    this.onLODChangeCallbacks.push(callback);
  }

  /**
   * Remove LOD change callback
   */
  removeLODChangeCallback(callback: onLODChangeCallback): void {
    const index = this.onLODChangeCallbacks.indexOf(callback);
    if (index >= 0) {
      this.onLODChangeCallbacks.splice(index, 1);
    }
  }

  /**
   * Notify all LOD change callbacks
   */
  private notifyLODChange(oldLevel: number, newLevel: number): void {
    for (const callback of this.onLODChangeCallbacks) {
      try {
        callback(oldLevel, newLevel);
      } catch (error) {
        // Silently handle callback errors
      }
    }
  }

  /**
   * Get controller statistics
   */
  getStats(): LODControllerStats {
    return {
      currentLevel: this.currentLODLevel.level,
      targetLevel: this.targetLODLevel,
      transitionState: this.transitionState,
      transitionStartTime: this.transitionStartTime,
      transitionDuration: this.getTransitionElapsedTime(),
      totalTransitions: this.totalTransitions,
      rapidTransitions: this.rapidTransitions,
    };
  }

  /**
   * Reset statistics
   */
  resetStats(): void {
    this.totalTransitions = 0;
    this.rapidTransitions = 0;
  }

  /**
   * Validate LOD configuration
   */
  validateConfiguration(): { isValid: boolean; issues: string[] } {
    const issues: string[] = [];
    
    if (this.lodLevels.length === 0) {
      issues.push('No LOD levels configured');
      return { isValid: false, issues };
    }
    
    // Check for unique levels
    const levels = this.lodLevels.map((lod) => lod.level);
    const uniqueLevels = new Set(levels);
    if (uniqueLevels.size !== levels.length) {
      issues.push('Duplicate LOD levels found');
    }
    
    // Check for valid thresholds
    for (const lod of this.lodLevels) {
      if (lod.fovThreshold.min >= lod.fovThreshold.max) {
        issues.push(`Invalid FOV threshold for level ${lod.level}: min >= max`);
      }
      
      if (lod.tilesPerFace <= 0) {
        issues.push(`Invalid tilesPerFace for level ${lod.level}: must be > 0`);
      }
      
      if (lod.priority <= 0) {
        issues.push(`Invalid priority for level ${lod.level}: must be > 0`);
      }
    }
    
    // Check threshold coverage (should cover all possible FOV values)
    const sortedLevels = [...this.lodLevels].sort((a, b) => a.fovThreshold.min - b.fovThreshold.min);
    for (let i = 0; i < sortedLevels.length - 1; i++) {
      const current = sortedLevels[i];
      const next = sortedLevels[i + 1];
      
      if (current.fovThreshold.max < next.fovThreshold.min) {
        issues.push(`Gap in FOV coverage between levels ${current.level} and ${next.level}`);
      }
    }
    
    return {
      isValid: issues.length === 0,
      issues,
    };
  }

  /**
   * Add event listener
   */
  on<K extends keyof LODControllerEvents>(event: K, handler: LODControllerEvents[K]): void {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, new Set());
    }
    this.eventHandlers.get(event)!.add(handler);
  }

  /**
   * Remove event listener
   */
  off<K extends keyof LODControllerEvents>(event: K, handler: LODControllerEvents[K]): void {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      handlers.delete(handler);
    }
  }

  /**
   * Emit event
   */
  private emit<K extends keyof LODControllerEvents>(
    event: K,
    ...args: Parameters<LODControllerEvents[K]>
  ): void {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      for (const handler of handlers) {
        try {
          (handler as any)(...args);
        } catch (error) {
          // Silently handle event errors
        }
      }
    }
  }

  /**
   * Dispose all resources
   */
  dispose(): void {
    this.onLODChangeCallbacks.length = 0;
    this.eventHandlers.clear();
    this.transitionState = TransitionState.STABLE;
    this.resetStats();
  }
}
